import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Wrench, Send, MessageCircle } from "lucide-react";

const WA_ADMIN = "6285261925686";
const SERVICES = [
  "Stage 1 Remap", "Pop & Bang", "RPM Limiter Extender", "O2 / Lambda Off",
  "Immobilizer Immo-Off", "Quickshifter Enable", "Decat / EGR Off",
];
const STATUS_STYLE = {
  Pending: "bg-red-500/15 text-red-400 border-red-700/40",
  "In Progress": "bg-zinc-500/15 text-zinc-400 border-zinc-700/40",
  Completed: "bg-emerald-500/15 text-emerald-400 border-emerald-700/40",
  Delivered: "bg-slate-500/15 text-slate-300 border-slate-600/40",
};
const STATUSES = ["Pending", "In Progress", "Completed", "Delivered"];

export default function FileService() {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", service_type: SERVICES[0], model: "", notes: "" });
  const [ticket, setTicket] = useState(null);
  const [tickets, setTickets] = useState([]);

  const loadTickets = async () => { if (isAdmin) setTickets((await api.get("/tickets")).data); };
  useEffect(() => { loadTickets(); }, [isAdmin]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.email || !form.model) { toast.error("Name, email and model required"); return; }
    try {
      const res = await api.post("/tickets", form);
      setTicket(res.data);
      toast.success("Ticket submitted");
      loadTickets();
    } catch { toast.error("Submit failed"); }
  };

  const waLink = () => {
    const msg = `Halo Admin TRMD Performance, request File Service. Ticket: ${ticket?.ticket_no}, Nama: ${form.name}, Motor: ${form.model}, Servis: ${form.service_type}`;
    return `https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(msg)}`;
  };

  const updateTicket = async (id, patch) => {
    try { await api.put(`/tickets/${id}`, patch); toast.success("Ticket updated"); loadTickets(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto" data-testid="file-service-container">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Wrench className="h-4 w-4" /> Page 06</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">ECU Tuning File Service</h1>
        <p className="text-slate-500 text-sm mt-1">Submit a custom remap request and track turnaround.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Request form */}
        <div className="rounded-2xl border border-slate-800 bg-[#0c1017] p-6">
          <h2 className="font-head font-bold text-xl uppercase text-white mb-4">New Tune Request</h2>
          {!ticket ? (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">Name</Label>
                  <Input data-testid="fs-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">Email</Label>
                  <Input data-testid="fs-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Motorcycle / Model</Label>
                <Input data-testid="fs-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="CBR250RR 2019" className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Service Type</Label>
                <select data-testid="fs-service-select" value={form.service_type} onChange={(e) => set("service_type", e.target.value)}
                  className="w-full h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-100 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400">
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-slate-400">Notes</Label>
                <Textarea data-testid="fs-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-[#121824] border-slate-700 text-slate-100 focus-visible:ring-cyan-400" />
              </div>
              <Button data-testid="file-service-submit-ticket-btn" onClick={submit}
                className="w-full bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
                <Send className="h-4 w-4 mr-1.5" /> Submit Request
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-emerald-950/30 border border-emerald-800/40 p-4">
                <div className="text-xs font-mono-tt text-emerald-400 uppercase tracking-wider">Ticket Number</div>
                <div className="font-head font-black text-2xl text-white">{ticket.ticket_no}</div>
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-mono-tt border bg-red-500/15 text-red-400 border-red-700/40">{ticket.status}</span>
              </div>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="fs-whatsapp-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-bold transition-colors">
                <MessageCircle className="h-5 w-5" /> Send Ticket to Admin (WhatsApp)
              </a>
              <Button data-testid="fs-new-ticket-btn" variant="ghost" onClick={() => { setTicket(null); setForm({ name: "", email: "", service_type: SERVICES[0], model: "", notes: "" }); }}
                className="w-full text-slate-400 hover:text-white hover:bg-slate-800">Submit another</Button>
            </div>
          )}
        </div>

        {/* Services list / admin queue */}
        <div className="space-y-4">
          {!isAdmin ? (
            <div className="rounded-2xl border border-slate-800 bg-[#0c1017] p-6">
              <h2 className="font-head font-bold text-xl uppercase text-white mb-4">Services Offered</h2>
              <ul className="space-y-2">
                {SERVICES.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-slate-300 font-mono-tt">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-[#0c1017] p-6">
              <h2 className="font-head font-bold text-xl uppercase text-white mb-4">Admin — Ticket Queue</h2>
              <div className="space-y-3 max-h-[560px] overflow-y-auto">
                {tickets.map((t) => (
                  <div key={t.id} className="rounded-lg border border-slate-800 bg-[#121824] p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-head font-bold text-white">{t.ticket_no}</span>
                      <span data-testid="file-service-status-badge" className={`px-2 py-0.5 rounded text-xs font-mono-tt border ${STATUS_STYLE[t.status]}`}>{t.status}</span>
                    </div>
                    <div className="text-xs font-mono-tt text-slate-400 mt-2 space-y-0.5">
                      <div>{t.name} · {t.email}</div>
                      <div>{t.model} — <span className="text-cyan-300">{t.service_type}</span></div>
                      {t.notes && <div className="text-slate-500">{t.notes}</div>}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <select data-testid={`fs-status-select-${t.id}`} value={t.status} onChange={(e) => updateTicket(t.id, { status: e.target.value })}
                        className="flex-1 h-9 rounded-md bg-[#0c1017] border border-slate-700 text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-400">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <Input data-testid={`fs-price-input-${t.id}`} defaultValue={t.price} placeholder="Price"
                        onBlur={(e) => e.target.value !== t.price && updateTicket(t.id, { price: e.target.value })}
                        className="w-24 h-9 bg-[#0c1017] border-slate-700 text-slate-100 text-xs" />
                    </div>
                  </div>
                ))}
                {tickets.length === 0 && <div className="text-center py-8 text-slate-600 font-mono-tt text-sm">No tickets yet.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
